import React, { useState, useEffect, useRef } from 'react';
import { ApSimpleSearch } from '@alixpartners/ui-components';
import { DropdownItem } from '../DropdownItem';
import {
	Wrapper,
	SearchWrapper,
	Header,
	CheckboxListWrapper,
	VirtualListWrapper,
	NoData,
	GroupLabel,
} from '../NavFilter.styles';
import { VirtualList } from '../VirtualList';

const VIRTUAL_LIST_THRESHOLD = 25;

export const FilterMenuContent = ({
	options,
	onChange,
	maxSelected,
	selected,
	isOpen,
	setShowVirtualList,
	groupLabels,
}) => {
	const wrapperRef = useRef();
	const scrollContainerRef = useRef();
	const [query, setQuery] = useState('');
	const [hasScroll, setHasScroll] = useState(false);

	const handleChange = value => {
		let newSelected = [];

		if (selected.includes(value)) {
			newSelected = selected.filter(item => item !== value);
		} else {
			newSelected = [...selected, value];
		}

		onChange(newSelected);
	};

	const getFilteredItems = () => {
		let processedItems = options.map(item => ({
			...item,
			selected: selected.includes(item.value),
			disabled: options.length <= 1,
		}));

		// Prevent from selection if selection exceeds max selected allowed
		if (maxSelected) {
			const selectedCount = options.reduce((acc, item) => {
				return acc + (item.selected ? 1 : 0);
			}, 0);

			processedItems = options.map(item => ({
				...item,
				disabled:
					(!item.selected && selectedCount >= maxSelected) ||
					options.length <= 1,
			}));
		}

		if (!query) {
			return processedItems;
		}

		return processedItems.filter(item =>
			item.label.toLowerCase().includes(query.toLowerCase())
		);
	};

	const setScrollState = () => {
		const srollContainer = scrollContainerRef && scrollContainerRef.current;

		if (srollContainer) {
			const hasScrollbar =
				srollContainer.scrollHeight > srollContainer.clientHeight;

			// Hide search input if no scrollbar
			setHasScroll(hasScrollbar);
		}
	};

	useEffect(
		() => {
			setScrollState();
		},
		[scrollContainerRef, isOpen]
	);

	useEffect(
		() => {
			if (isOpen === false) {
				setQuery('');
			}
		},
		[isOpen]
	);

	useEffect(() => {
		window.addEventListener('resize', setScrollState);

		return () => {
			window.removeEventListener('resize', setScrollState);
		};
	}, []);

	const filteredItems = getFilteredItems();
	const isVirtualList = filteredItems.length >= VIRTUAL_LIST_THRESHOLD;
	const isAllSelected = () => {
		return (
			filteredItems.length > 0 && filteredItems.every(item => item.selected)
		);
	};

	useEffect(
		() => {
			if (setShowVirtualList) {
				setShowVirtualList(isVirtualList);
			}

			return () => {
				setShowVirtualList(false);
			};
		},
		[setShowVirtualList, isVirtualList]
	);

	const isPartialSelected = () => {
		return !isAllSelected() && filteredItems.some(item => item.selected);
	};

	const selectAll = () => {
		let newSelected = [];

		if (!query) {
			if (selected.length !== options.length) {
				newSelected = options.map(item => item.value);
			}
		} else {
			const selectedPatch = filteredItems.map(item => item.value);

			if (!isAllSelected()) {
				newSelected = [
					...selected.filter(item => selectedPatch.includes(item.value)),
					...selectedPatch,
				];
			} else {
				newSelected = selected.filter(item =>
					selectedPatch.includes(item.value)
				);
			}
		}

		onChange(newSelected);
	};

	let groupName = null;

	return (
		<Wrapper>
			<Header>
				{(query || hasScroll || isVirtualList) && (
					<SearchWrapper>
						<ApSimpleSearch
							id="filter-search"
							onChange={value => setQuery(value)}
							onClear={() => setQuery('')}
						/>
					</SearchWrapper>
				)}
				<DropdownItem
					small
					suppressHover
					onChange={selectAll}
					partial={isPartialSelected()}
					selected={isAllSelected()}
					disabled={
						maxSelected || filteredItems.length === 0 || options.length <= 1
					}
					label={`${filteredItems.length} item${
						filteredItems.length !== 1 ? 's' : ''
					}`}
				/>
			</Header>
			{filteredItems.length === 0 ? (
				<NoData>No results found</NoData>
			) : !isVirtualList ? (
				<CheckboxListWrapper innerRef={scrollContainerRef}>
					{filteredItems.map((item, index) => {
						const showGroupLabel = item.group && groupName !== item.group;

						if (showGroupLabel) {
							groupName = item.group;
						}

						return (
							<React.Fragment key={index}>
								{showGroupLabel && (
									<GroupLabel>
										{(groupLabels && groupLabels[item.group]) || item.group}
									</GroupLabel>
								)}
								<DropdownItem
									{...item}
									searchQuery={query}
									onChange={handleChange}
								/>
							</React.Fragment>
						);
					})}
				</CheckboxListWrapper>
			) : (
				<VirtualListWrapper innerRef={wrapperRef}>
					<VirtualList
						wrapperRef={wrapperRef}
						data={filteredItems}
						searchQuery={query}
						onChange={handleChange}
					/>
				</VirtualListWrapper>
			)}
		</Wrapper>
	);
};
